import { useMemo } from 'react';
import type { AnalyticsClient, OgAccountTraits, OgVisitorTraits } from '@opengov/analytics';
import { useShellAnalytics, type ResolvedIdentity } from '@opengov/analytics/shell';

import { useAuth } from './useAuth';
import { useEntity } from '../contexts/EntityContext';
import { extractPlatformUser } from '../utils/auth0UserUtils';
import { buildVisitorTraits, buildAccountTraits } from './useSegmentIdentify';
import { useAnalyticsSupplementalData } from './useAnalyticsSupplementalData';
import { useOgAnalyticsEnabled } from './useOgAnalyticsEnabled';

/**
 * PSP's analytics wiring.
 *
 * Everything generic — the shared client, browser-history route tracking,
 * route→product attribution, identity de-duplication — lives in
 * `@opengov/analytics/shell`. All that remains here is the part only PSP can
 * know: **where identity comes from** (Auth0 claims, Entity Service,
 * Entitlements API).
 */

/** PSP's trait builders return `Record<string, unknown>`; the SDK requires flat
 *  primitives, because nested values cannot be filtered on in PostHog. Drop
 *  anything non-flat rather than send something unqueryable. */
function toTraits<T>(input: Record<string, unknown>): T {
  const out: Record<string, string | number | boolean | null | undefined> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v == null || ['string', 'number', 'boolean'].includes(typeof v)) {
      out[k] = v as string | number | boolean | null | undefined;
    }
  }
  return out as T;
}

export function useOgAnalytics(): AnalyticsClient | null {
  const { user } = useAuth();
  const { activeEntity, activeEntityEntitledApplications } = useEntity();
  const platformUser = extractPlatformUser(user);
  const enabled = useOgAnalyticsEnabled();

  // Six traits live in two APIs rather than in the Auth0 token. They were being
  // passed as `undefined` here, which left `og_department`, `psp_role_display`,
  // `og_country`, `og_region`, `og_city` and `og_salesforceid` empty for every
  // user — so any segment keyed on role, department, geography or Salesforce ID
  // returned nothing on the new SDK while it still worked on Segment.
  const { department, roleDisplayName, fullEntity } = useAnalyticsSupplementalData(enabled);

  const identity: ResolvedIdentity | null = useMemo(() => {
    if (!platformUser?.uuid || !activeEntity || !user) return null;
    const entitledProducts = activeEntityEntitledApplications.map((a) => a.displayName).join(', ');
    return {
      visitorId: platformUser.uuid,
      visitorTraits: toTraits<OgVisitorTraits>(
        buildVisitorTraits(
          user,
          platformUser.delphius_role,
          // The UUID, matching `accountId` below and `$groups.account`.
          activeEntity.id,
          activeEntity.friendlyId,
          department,
          roleDisplayName
        )
      ),
      accountId: activeEntity.id,
      accountTraits: toTraits<OgAccountTraits>(
        buildAccountTraits(activeEntity.name, activeEntity.friendlyId, entitledProducts, fullEntity)
      ),
      // All six async traits belong in the fingerprint. `useShellAnalytics`
      // re-identifies only when this changes, so any trait left out keeps the
      // value from the first identify — fired before the two queries resolve,
      // with the traits still empty — for the rest of the session. Carrying
      // only some of them missed the rest whenever those few were empty.
      // Entitlements too: `og_products` is built from them, and they can change
      // mid-session (an admin grants an app), which must re-identify.
      fingerprint: [
        platformUser.uuid,
        activeEntity.friendlyId,
        department ?? '',
        roleDisplayName ?? '',
        fullEntity?.address?.country ?? '',
        fullEntity?.address?.region ?? '',
        fullEntity?.address?.city ?? '',
        fullEntity?.thirdPartyIds?.salesforceId ?? '',
        entitledProducts,
      ].join(':'),
    };
  }, [
    user,
    platformUser?.uuid,
    platformUser?.delphius_role,
    activeEntity,
    activeEntityEntitledApplications,
    department,
    roleDisplayName,
    fullEntity,
  ]);

  return useShellAnalytics({
    // No product information passed. `og_product` is resolved by the SDK from
    // the path against its own table, so PSP cannot mis-attribute a product —
    // and a new app needs no change here.
    identity,
    enabled,
    collapseSegment: activeEntity?.friendlyId,
    // fallbackProduct defaults to the client's own `app` ('psp') — nothing to repeat
  });
}
